import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Boxes, Loader2, RefreshCw, Plus, X, Trash2, History, ChevronDown, ChevronRight,
  ArrowRight, ShieldCheck, User,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';

const STATUS_STYLE = {
  draft: 'bg-muted/40 border-border/40 text-muted-foreground',
  validated: 'bg-sky-500/10 border-sky-500/30 text-sky-600 dark:text-sky-300',
  published: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300',
  deprecated: 'bg-rose-500/10 border-rose-500/30 text-rose-600 dark:text-rose-300',
};
const STATUS_LABEL = { draft: 'Draft', validated: 'Validated', published: 'Published', deprecated: 'Deprecated' };
const TYPE_LABEL = {
  early_warning: 'Early warning', propensity: 'Propensity', severity: 'Severity',
  frequency: 'Frequency', ultimate_loss: 'Ultimate loss', forecast: 'Forecast', other: 'Other',
};
const METHOD_LABEL = { deterministic_baseline: 'Deterministic', statistical: 'Statistical', ml_external: 'External ML' };
const SIGNAL_STATUS_STYLE = {
  alert: 'bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-300',
  warning: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-300',
  ok: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300',
};
const DRIFT_STYLE = {
  critical: 'bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-300',
  drifting: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-300',
  stable: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300',
  insufficient_data: 'bg-muted/40 border-border/40 text-muted-foreground',
};
const DRIFT_LABEL = { critical: 'Drift: critical', drifting: 'Drift: drifting', stable: 'Drift: stable', insufficient_data: 'Drift: n/a' };
const driftTip = (drift) => Object.values(drift?.signals || {}).map((s) => s.explanation).filter(Boolean).join('\n') || 'Not enough runs to assess drift yet.';

const EMPTY_FORM = {
  name: '', description: '', model_type: 'early_warning', method: 'deterministic_baseline',
  grain: '', target: '', features: '', business_owner: '', technical_owner: '',
};

export default function PredictiveModelsPage() {
  const canManage = useCan('measures:certify');
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [options, setOptions] = useState({ model_types: [], methods: [], statuses: [], transitions: {}, measures: [] });
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(new Set());
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [expanded, setExpanded] = useState(new Set());
  const [searchParams] = useSearchParams();

  // Deep-link from the Drift Alert Center: pre-select the alerting model's scorecard.
  useEffect(() => {
    const m = searchParams.get('model');
    if (m) setModelId(m);
  }, [searchParams]);

  useEffect(() => {
    api.getSemanticModels().then((r) => setModels(r.models || r || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.getPredictiveOptions(modelId).then(setOptions).catch(() => {});
  }, [modelId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getPredictiveModels(modelId, statusFilter);
      setEntries(r.models || []);
      setCounts(r.counts || {});
    } catch (e) {
      toast.error(e.message || 'Failed to load predictive models');
    } finally {
      setLoading(false);
    }
  }, [modelId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const withBusy = async (key, fn, okMsg) => {
    setBusy((p) => new Set(p).add(key));
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.message || 'Action failed'); }
    finally { setBusy((p) => { const s = new Set(p); s.delete(key); return s; }); }
  };

  const createModel = () => withBusy('create', async () => {
    if (!modelId) throw new Error('Pick a semantic model first (top selector)');
    if (!form.name.trim()) throw new Error('Name is required');
    await api.createPredictiveModel({
      model_id: modelId,
      name: form.name.trim(), description: form.description.trim(),
      model_type: form.model_type, method: form.method, grain: form.grain.trim(),
      target: { measure: form.target },
      features: form.features.split(',').map((s) => s.trim()).filter(Boolean),
      owners: { business_owner: form.business_owner.trim(), technical_owner: form.technical_owner.trim() },
    });
    setForm(EMPTY_FORM);
    setShowNew(false);
  }, 'Predictive model registered');

  const transition = (e, to) => withBusy(`t-${e.pmodel_id}-${to}`,
    () => api.transitionPredictiveModel(e.pmodel_id, to), `Moved to ${STATUS_LABEL[to] || to}`);
  const remove = (e) => withBusy(`d-${e.pmodel_id}`,
    () => api.deletePredictiveModel(e.pmodel_id), 'Draft deleted');

  const toggleExpand = (id) => setExpanded((p) => {
    const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s;
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="predictive-models-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-heading font-semibold flex items-center gap-2">
            <Boxes className="w-5 h-5 text-indigo-400" /> Predictive Model Registry
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Govern predictive &amp; insurance-risk models the way you certify metrics — versioned, owned, and lifecycle-controlled. Definitions only, no customer values.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button size="sm" onClick={() => setShowNew((v) => !v)} className="gap-1 text-xs h-7" data-testid="pm-new">
              <Plus className="w-3 h-3" /> Register model
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={load} className="gap-1 text-xs h-7" data-testid="pm-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Model</Label>
          <select value={modelId} onChange={(e) => setModelId(e.target.value)}
            className="h-8 rounded-md border border-border/50 bg-background px-2 text-xs" data-testid="pm-model-select">
            <option value="">All models</option>
            {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1" data-testid="pm-status-filter">
          {['', ...(options.statuses || [])].map((s) => (
            <button key={s || 'all'} onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 rounded-md text-[11px] border ${statusFilter === s ? 'border-primary text-foreground bg-primary/10' : 'border-border/40 text-muted-foreground'}`}
              data-testid={`pm-filter-${s || 'all'}`}>
              {s ? STATUS_LABEL[s] : 'All'}{s && counts[s] != null ? ` (${counts[s]})` : ''}
            </button>
          ))}
        </div>
      </div>

      {showNew && canManage && (
        <div className="rounded-xl border border-border/40 bg-card/30 p-4 space-y-3" data-testid="pm-new-form">
          {!modelId && <p className="text-[11px] text-amber-500">Select a semantic model in the top selector to register against it.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Name</Label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. LOB loss-ratio early warning"
                className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="pm-form-name" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Grain</Label>
              <input value={form.grain} onChange={(e) => setForm((f) => ({ ...f, grain: e.target.value }))}
                placeholder="e.g. line of business × month"
                className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="pm-form-grain" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Type</Label>
              <select value={form.model_type} onChange={(e) => setForm((f) => ({ ...f, model_type: e.target.value }))}
                className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="pm-form-type">
                {(options.model_types || []).map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Method</Label>
              <select value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}
                className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="pm-form-method">
                {(options.methods || []).map((m) => <option key={m} value={m}>{METHOD_LABEL[m] || m}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Target measure</Label>
              <select value={form.target} onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
                className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="pm-form-target">
                <option value="">(none)</option>
                {(options.measures || []).map((m) => <option key={m.name} value={m.name}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Features (comma-separated)</Label>
              <input value={form.features} onChange={(e) => setForm((f) => ({ ...f, features: e.target.value }))}
                placeholder="e.g. frequency, severity, reserve development"
                className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="pm-form-features" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Business owner</Label>
              <input value={form.business_owner} onChange={(e) => setForm((f) => ({ ...f, business_owner: e.target.value }))}
                className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="pm-form-bowner" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Technical owner</Label>
              <input value={form.technical_owner} onChange={(e) => setForm((f) => ({ ...f, technical_owner: e.target.value }))}
                className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="pm-form-towner" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Description</Label>
            <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2} className="w-full px-2 py-1 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="pm-form-desc" />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={createModel} disabled={busy.has('create')} className="h-8 text-xs" data-testid="pm-form-submit">
              {busy.has('create') ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Register'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowNew(false)} className="h-8 text-xs"><X className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : entries.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="pm-empty">
          No predictive models registered{statusFilter ? ` in "${STATUS_LABEL[statusFilter]}"` : ''}. {canManage ? 'Register one above to start the predictive rung.' : ''}
        </div>
      ) : (
        <div className="space-y-2" data-testid="pm-list">
          {entries.map((e) => {
            const nexts = (options.transitions || {})[e.status] || [];
            const isOpen = expanded.has(e.pmodel_id);
            return (
              <div key={e.pmodel_id} className="rounded-xl border border-border/40 bg-card/30 p-3 space-y-2" data-testid={`pm-item-${e.pmodel_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <Badge variant="outline" className={`text-[9px] ${STATUS_STYLE[e.status] || ''}`} data-testid={`pm-status-${e.pmodel_id}`}>
                        {STATUS_LABEL[e.status] || e.status}
                      </Badge>
                      <Badge variant="outline" className="text-[9px] border-indigo-500/30 text-indigo-600 dark:text-indigo-300">{TYPE_LABEL[e.model_type] || e.model_type}</Badge>
                      <Badge variant="outline" className="text-[9px] border-border/40 text-muted-foreground">{METHOD_LABEL[e.method] || e.method}</Badge>
                      <span className="text-[10px] text-muted-foreground">v{e.version}</span>
                    </div>
                    <p className="font-medium text-sm text-foreground">{e.name || '(unnamed)'}</p>
                    {e.description && <p className="text-[11px] text-muted-foreground mt-0.5">{e.description}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                      {e.target?.measure && <span>target: <span className="text-foreground">{e.target.measure.replace(/_/g, ' ')}</span></span>}
                      {e.grain && <span>grain: {e.grain}</span>}
                      {e.features?.length > 0 && <span>{e.features.length} feature{e.features.length > 1 ? 's' : ''}</span>}
                      {(e.owners?.business_owner || e.owners?.technical_owner) && (
                        <span className="flex items-center gap-1"><User className="w-2.5 h-2.5" />{[e.owners?.business_owner, e.owners?.technical_owner].filter(Boolean).join(' / ')}</span>
                      )}
                    </div>
                    {(e.backtest?.value != null || e.last_run || e.drift) && (
                      <div className="flex flex-wrap items-center gap-2 mt-1.5" data-testid={`pm-scorecard-${e.pmodel_id}`}>
                        {e.backtest?.value != null && (
                          <Badge variant="outline" className="text-[9px] border-cyan-500/30 text-cyan-600 dark:text-cyan-300" data-testid={`pm-backtest-${e.pmodel_id}`}>
                            backtest {e.backtest.metric || 'score'}: {e.backtest.value}{e.backtest.window ? ` (${e.backtest.window})` : ''}
                          </Badge>
                        )}
                        {e.last_run && (
                          <Badge variant="outline" className={`text-[9px] ${SIGNAL_STATUS_STYLE[e.last_run.status] || 'border-border/40 text-muted-foreground'}`} data-testid={`pm-lastrun-${e.pmodel_id}`}>
                            signals: {e.last_run.status || '—'}{(e.last_run.alert || e.last_run.warning) ? ` · ${e.last_run.alert || 0}A/${e.last_run.warning || 0}W` : ''}
                          </Badge>
                        )}
                        {e.drift?.overall && (
                          <Badge variant="outline" title={driftTip(e.drift)} className={`text-[9px] ${DRIFT_STYLE[e.drift.overall] || DRIFT_STYLE.insufficient_data}`} data-testid={`pm-drift-${e.pmodel_id}`}>
                            {DRIFT_LABEL[e.drift.overall] || e.drift.overall}
                          </Badge>
                        )}
                        {e.drift?.needs_recertification && (
                          <Badge variant="outline" title={driftTip(e.drift)} className="text-[9px] border-rose-500/40 text-rose-600 dark:text-rose-300 gap-1" data-testid={`pm-needs-recert-${e.pmodel_id}`}>
                            <ShieldCheck className="w-2.5 h-2.5" /> Needs recertification
                          </Badge>
                        )}
                        {e.last_run?.generated_at && (
                          <span className="text-[10px] text-muted-foreground">last run {new Date(e.last_run.generated_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    )}
                  </div>
                  {canManage && (
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <div className="flex items-center gap-1 flex-wrap justify-end">
                        {nexts.map((ns) => (
                          <Button key={ns} size="sm" variant={ns === 'published' ? 'default' : 'outline'}
                            onClick={() => transition(e, ns)} disabled={busy.has(`t-${e.pmodel_id}-${ns}`)}
                            className={`h-6 text-[10px] gap-1 ${ns === 'published' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : ''}`}
                            data-testid={`pm-to-${ns}-${e.pmodel_id}`}>
                            {ns === 'published' ? <ShieldCheck className="w-2.5 h-2.5" /> : <ArrowRight className="w-2.5 h-2.5" />}
                            {STATUS_LABEL[ns] || ns}
                          </Button>
                        ))}
                      </div>
                      {e.status === 'draft' && (
                        <Button size="sm" variant="ghost" onClick={() => remove(e)} disabled={busy.has(`d-${e.pmodel_id}`)}
                          className="h-6 text-[10px] text-rose-500 gap-1" data-testid={`pm-delete-${e.pmodel_id}`}>
                          <Trash2 className="w-2.5 h-2.5" /> Delete
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={() => toggleExpand(e.pmodel_id)} className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
                  data-testid={`pm-history-toggle-${e.pmodel_id}`}>
                  {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  <History className="w-3 h-3" /> History ({(e.history || []).length})
                </button>
                {isOpen && (
                  <div className="pl-4 border-l border-border/40 space-y-1" data-testid={`pm-history-${e.pmodel_id}`}>
                    {(e.history || []).slice().reverse().map((h, i) => (
                      <div key={i} className="text-[11px] text-muted-foreground flex items-center gap-2">
                        <Badge variant="outline" className={`text-[8px] ${STATUS_STYLE[h.status] || ''}`}>{STATUS_LABEL[h.status] || h.status}</Badge>
                        <span>v{h.version}</span>
                        <span>{h.note}</span>
                        {h.at && <span className="opacity-60">{new Date(h.at).toLocaleString()}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { PredictiveModelsPage };
